import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import { 
  Upload, Download, Loader2, CheckCircle, XCircle, 
  AlertTriangle, Clock, Globe, Play, Link2, RefreshCw, Eye, Bell, ArrowRightLeft
} from "lucide-react";
import LinkMonitorTab from "./LinkMonitorTab";
import CompareToolTab from "./CompareToolTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGES = {
  online: { color: "bg-green-500/20 text-green-600 border-green-500/30", icon: CheckCircle, label: "Online" },
  not_found: { color: "bg-red-500/20 text-red-600 border-red-500/30", icon: XCircle, label: "No encontrado" },
  timeout: { color: "bg-yellow-500/20 text-yellow-600 border-yellow-500/30", icon: Clock, label: "Timeout" },
  error: { color: "bg-red-500/20 text-red-600 border-red-500/30", icon: AlertTriangle, label: "Error" },
  connection_error: { color: "bg-red-500/20 text-red-600 border-red-500/30", icon: XCircle, label: "Sin conexión" },
  redirect: { color: "bg-blue-500/20 text-blue-600 border-blue-500/30", icon: Link2, label: "Redirección" },
  forbidden: { color: "bg-orange-500/20 text-orange-600 border-orange-500/30", icon: AlertTriangle, label: "Prohibido" },
  server_error: { color: "bg-red-500/20 text-red-600 border-red-500/30", icon: AlertTriangle, label: "Error servidor" },
};

const UrlCheckerTab = () => {
  const [activeSubTab, setActiveSubTab] = useState("checker");
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [results, setResults] = useState([]);
  const [filter, setFilter] = useState("all");
  const [urlsText, setUrlsText] = useState("");
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!job || job.status === "completed" || job.status === "error" || job.status === "cancelled") return;
    
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API}/url-checker/job/${job.job_id}`);
        const data = await response.json();
        setJob(data);
        
        if (data.status === "completed") {
          toast.success("Verificación completada");
          loadResults(data.job_id, filter);
        }
      } catch (error) {
        console.error("Error polling job:", error);
      }
    }, 2000);
    
    return () => clearInterval(interval);
  }, [job, filter]);

  const handleCheckUrls = async () => {
    if (!urlsText.trim()) {
      toast.error("Pega algunas URLs para verificar");
      return;
    }
    
    setChecking(true);
    setJob(null);
    setResults([]);
    
    try {
      const response = await fetch(`${API}/url-checker/check-text`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ urls_text: urlsText })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setJob({ 
          job_id: data.job_id,
          total: data.total_urls,
          status: "processing", 
          processed: 0, 
          progress_percent: 0,
          online_count: 0,
          offline_count: 0,
          other_count: 0
        });
        toast.success(`Verificando ${data.total_urls} URLs...`);
      } else {
        toast.error(data.detail || "Error al verificar URLs");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
    setChecking(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    setJob(null);
    setResults([]);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await fetch(`${API}/url-checker/upload-large`, {
        method: "POST",
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setJob({ 
          job_id: data.job_id,
          total: data.total_urls,
          status: "processing", 
          processed: 0, 
          progress_percent: 0,
          online_count: 0,
          offline_count: 0,
          other_count: 0
        });
        toast.success(`Verificando ${data.total_urls} URLs...`);
      } else {
        toast.error(data.detail || "Error al subir archivo");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
    setUploading(false);
    e.target.value = '';
  };

  const loadResults = async (jobId, filterType) => {
    try {
      const response = await fetch(`${API}/url-checker/job/${jobId}/results?filter=${filterType}`);
      const data = await response.json();
      setResults(data.results || []);
    } catch (error) {
      toast.error("Error al cargar resultados");
    }
  };

  const downloadResults = async (filterType) => {
    if (!job) return;
    
    try {
      const response = await fetch(`${API}/url-checker/job/${job.job_id}/export?filter=${filterType}&format=excel`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `urls_${filterType}_${new Date().toISOString().slice(0,10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Excel descargado");
      } else {
        toast.error("Error al descargar");
      }
    } catch (error) {
      toast.error("Error al descargar");
    }
  };

  const handleReset = () => {
    setJob(null);
    setResults([]);
    setUrlsText("");
    setFilter("all");
  };

  const StatusBadge = ({ status }) => {
    const config = STATUS_BADGES[status] || STATUS_BADGES.error;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label || status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="url-checker-tab">
      {/* Tabs for Checker, Monitor and Compare */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="checker" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Verificador
          </TabsTrigger>
          <TabsTrigger value="monitor" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Monitor
          </TabsTrigger>
          <TabsTrigger value="compare" className="flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4" />
            Comparar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="checker" className="space-y-6 mt-6">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Globe className="h-6 w-6 text-green-600" />
                Verificador Masivo de URLs
              </h2>
              <p className="text-muted-foreground">Comprueba el estado de miles de URLs en paralelo</p>
            </div>
            {(job || results.length > 0) && (
              <Button variant="outline" onClick={handleReset}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Nueva verificación
              </Button>
            )}
          </div>

          {/* Input Section */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Text Input */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Globe className="w-5 h-5 text-green-600" />
                  Pega las URLs
                </CardTitle>
                <CardDescription>Una URL por línea (máximo 5,000)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder={"https://ejemplo1.com\nhttps://ejemplo2.com\nexample.org\n..."}
                  value={urlsText}
                  onChange={(e) => setUrlsText(e.target.value)}
                  className="min-h-[200px] font-mono text-sm"
                  disabled={checking || (job && job.status === "processing")}
            />
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {urlsText.trim() ? `${urlsText.trim().split('\n').filter(l => l.trim()).length} URLs` : "0 URLs"}
              </span>
              <Button 
                onClick={handleCheckUrls} 
                disabled={checking || !urlsText.trim() || (job && job.status === "processing")}
                className="bg-green-600 hover:bg-green-700"
              >
                {checking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                Verificar URLs
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* File Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              O sube un archivo
            </CardTitle>
            <CardDescription>CSV, Excel (.xlsx) o TXT con URLs</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
              <Input
                type="file"
                accept=".xlsx,.xls,.csv,.txt"
                onChange={handleUpload}
                disabled={uploading || checking || (job && job.status === "processing")}
                className="max-w-xs mx-auto"
              />
              <p className="text-sm text-muted-foreground mt-2">
                La primera columna debe contener las URLs
              </p>
            </div>
            {uploading && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Procesando archivo...
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Progress Section */}
      {job && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Progreso de verificación</span>
              <Badge variant={job.status === "completed" ? "default" : "secondary"}>
                {job.status === "completed" ? "Completado" : job.status === "error" ? "Error" : "Procesando..."}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Progress value={job.progress_percent || 0} className="h-3" />
            
            <div className="grid grid-cols-4 gap-4 text-center">
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold">{job.processed || 0}</p>
                <p className="text-xs text-muted-foreground">de {job.total} URLs</p>
              </div>
              <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                <p className="text-2xl font-bold text-green-600">{job.online_count || 0}</p>
                <p className="text-xs text-green-600">Online</p>
              </div>
              <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                <p className="text-2xl font-bold text-red-600">{job.offline_count || 0}</p>
                <p className="text-xs text-red-600">Offline/Error</p>
              </div>
              <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                <p className="text-2xl font-bold text-yellow-600">{job.other_count || 0}</p>
                <p className="text-xs text-yellow-600">Otros</p>
              </div>
            </div>

            {job.status === "completed" && (
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <Button 
                  onClick={() => { setFilter("all"); loadResults(job.job_id, "all"); }} 
                  variant={filter === "all" ? "default" : "outline"} 
                  size="sm"
                >
                  Todas ({job.total})
                </Button>
                <Button 
                  onClick={() => { setFilter("online"); loadResults(job.job_id, "online"); }}
                  variant={filter === "online" ? "default" : "outline"} 
                  size="sm"
                  className={filter === "online" ? "bg-green-600 hover:bg-green-700" : ""}
                >
                  Online ({job.online_count})
                </Button>
                <Button 
                  onClick={() => { setFilter("offline"); loadResults(job.job_id, "offline"); }}
                  variant={filter === "offline" ? "default" : "outline"} 
                  size="sm"
                  className={filter === "offline" ? "bg-red-600 hover:bg-red-700" : ""}
                >
                  Offline ({job.offline_count})
                </Button>
                <div className="flex-1" />
                <Button onClick={() => downloadResults(filter)} variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-1" />
                  Descargar Excel
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results Table */}
      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resultados ({results.length})</CardTitle>
            <CardDescription>
              {filter === "all" ? "Todas las URLs" : filter === "online" ? "URLs online" : "URLs offline/error"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-[500px] overflow-y-auto rounded-lg border">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead className="w-[40%]">URL</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Tiempo</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.slice(0, 200).map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono text-xs max-w-xs">
                        <a 
                          href={r.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-blue-600 hover:underline truncate block"
                        >
                          {r.url}
                        </a>
                      </TableCell>
                      <TableCell><StatusBadge status={r.status} /></TableCell>
                      <TableCell className="text-center">{r.status_code || "-"}</TableCell>
                      <TableCell>{r.response_time_ms ? `${r.response_time_ms}ms` : "-"}</TableCell>
                      <TableCell className="text-xs text-red-500 max-w-[200px] truncate">{r.error || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {results.length > 200 && (
              <p className="text-sm text-muted-foreground text-center mt-4">
                Mostrando 200 de {results.length} resultados. Descarga el Excel para ver todos.
              </p>
            )}
          </CardContent>
        </Card>
      )}
        </TabsContent>

        <TabsContent value="monitor" className="mt-6">
          <LinkMonitorTab />
        </TabsContent>

        <TabsContent value="compare" className="mt-6">
          <CompareToolTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UrlCheckerTab;
